'use client';

import Image from 'next/image';
import { motion, useReducedMotion } from 'motion/react';
import badgeIcon from '@images/link-copied-badge.svg';
import checkIcon from '@images/link-copied-check.svg';

/**
 * 아이콘 등장 스프링 — Figma 타임라인의 감쇠 진동(ω≈22.3, ζ≈0.32)을
 * stiffness/damping 으로 옮긴 값이다.
 */
const POP_SPRING = {
  type: 'spring',
  stiffness: 557,
  damping: 15,
  mass: 1,
} as const;

/** 체크 표시는 배지가 자리를 잡은 뒤에 그려진다 */
const CHECK_DELAY = 0.525;

/**
 * 복사 완료 배지 + 체크 아이콘. 등장 모션을 들고 있다.
 *
 * 데스크톱 토스트(36×34.506)와 모바일 공유 버튼 인라인(20×19.17)이 크기만 달리
 * 해서 같이 쓴다. **motion 런타임을 이 청크에 가둬 두는 것이 이 파일의 목적**이므로,
 * 초기 번들에 있는 곳에서는 `dynamic()` 으로 불러야 한다. 크기는 호출자가
 * `className` 으로 정한다(가로·세로 둘 다 줄 것).
 */
export default function CopiedBadgeIcon({ className }: { className?: string }) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <span className={`relative block shrink-0 ${className ?? ''}`}>
      {/* 이미지가 아니라 래퍼를 변형한다 */}
      <motion.span
        className="absolute inset-[8.33%_8.34%_8.33%_8.33%] block"
        initial={
          shouldReduceMotion ? false : { opacity: 0, scale: 0, rotate: 360 }
        }
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{
          opacity: { duration: 0.225, ease: 'easeOut' },
          rotate: { duration: 1.05, ease: 'easeOut' },
          scale: POP_SPRING,
        }}
      >
        <Image src={badgeIcon} alt="" aria-hidden className="size-full" />
      </motion.span>
      {/* 좌우 inset 을 함께 0.5px 옮겨 크기는 그대로 두고 광학 중심만 오른쪽으로
          맞춘다. transform 은 motion 이 인라인으로 덮어쓰므로 쓸 수 없다. */}
      <motion.span
        className="absolute inset-[36.14%_calc(29.56%_-_0.5px)_31.48%_calc(26.93%_+_0.5px)] block"
        initial={shouldReduceMotion ? false : { opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          opacity: { duration: 0.3, delay: CHECK_DELAY, ease: 'easeOut' },
          scale: { ...POP_SPRING, delay: CHECK_DELAY },
        }}
      >
        <Image src={checkIcon} alt="" aria-hidden className="size-full" />
      </motion.span>
    </span>
  );
}
